import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  PaymentMethod,
  PaymentMethodCreateRequest,
  PaymentMethodUpdateRequest,
  PaymentMethodQueryParams,
  PaymentMethodsData,
} from '../../types/api/payment-method';

export class PaymentMethodService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  async getPaymentMethods(params: PaymentMethodQueryParams = {}): Promise<PaymentMethodsData> {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', String(params.page));
    if (params.size !== undefined) queryParams.append('size', String(params.size));

    const url = `${API_ENDPOINTS.PAYMENT_METHODS.ADMIN_LIST}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await this.httpClient.get<PaymentMethodsData>(url);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch payment methods');
    }
    return response.data;
  }

  async getPaymentMethod(paymentMethodId: string): Promise<PaymentMethod> {
    const response = await this.httpClient.get<PaymentMethod>(
      API_ENDPOINTS.PAYMENT_METHODS.ADMIN_DETAIL(paymentMethodId)
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch payment method');
    }
    return response.data;
  }

  async createWithFiles(
    payload: PaymentMethodCreateRequest,
    logoFile?: File,
    qrImageFile?: File
  ): Promise<PaymentMethod> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    formData.append('data', jsonBlob, 'data.json');

    if (logoFile) {
      formData.append('logo', logoFile, logoFile.name);
    }
    if (qrImageFile) {
      formData.append('qrImage', qrImageFile, qrImageFile.name);
    }

    const response = await this.httpClient.request<PaymentMethod>({
      method: 'POST',
      url: API_ENDPOINTS.PAYMENT_METHODS.ADMIN_CREATE_MULTIPART,
      data: formData,
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to create payment method');
    }
    return response.data;
  }

  async createWithJson(payload: PaymentMethodCreateRequest): Promise<PaymentMethod> {
    const response = await this.httpClient.post<PaymentMethod>(
      API_ENDPOINTS.PAYMENT_METHODS.ADMIN_CREATE_JSON,
      payload
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to create payment method');
    }
    return response.data;
  }

  async updateWithFiles(
    paymentMethodId: string,
    payload: PaymentMethodUpdateRequest,
    logoFile?: File,
    qrImageFile?: File
  ): Promise<PaymentMethod> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    formData.append('data', jsonBlob, 'data.json');

    if (logoFile) {
      formData.append('logo', logoFile, logoFile.name);
    }
    if (qrImageFile) {
      formData.append('qrImage', qrImageFile, qrImageFile.name);
    }

    const response = await this.httpClient.request<PaymentMethod>({
      method: 'PUT',
      url: API_ENDPOINTS.PAYMENT_METHODS.ADMIN_UPDATE_MULTIPART(paymentMethodId),
      data: formData,
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update payment method');
    }
    return response.data;
  }

  async updateWithJson(
    paymentMethodId: string,
    payload: PaymentMethodUpdateRequest
  ): Promise<PaymentMethod> {
    const response = await this.httpClient.put<PaymentMethod>(
      API_ENDPOINTS.PAYMENT_METHODS.ADMIN_UPDATE_JSON(paymentMethodId),
      payload
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to update payment method');
    }
    return response.data;
  }

  async activate(paymentMethodId: string): Promise<void> {
    const response = await this.httpClient.put(
      API_ENDPOINTS.PAYMENT_METHODS.ADMIN_ACTIVATE(paymentMethodId)
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to activate payment method');
    }
  }

  async deactivate(paymentMethodId: string): Promise<void> {
    const response = await this.httpClient.put(
      API_ENDPOINTS.PAYMENT_METHODS.ADMIN_DEACTIVATE(paymentMethodId)
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to deactivate payment method');
    }
  }

  async delete(paymentMethodId: string): Promise<void> {
    const response = await this.httpClient.delete(
      API_ENDPOINTS.PAYMENT_METHODS.ADMIN_DELETE(paymentMethodId)
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete payment method');
    }
  }
}

